import React, { useEffect, useMemo, useState } from 'react';
import api from './api';
import TaskModal from './TaskModal';
import { taskCode, formatDate, PURCHASE_STATUSES, PURCHASE_STATUS_LABELS, PURCHASE_STATUS_BADGE } from './helpers';

/**
 * Suivi des demandes d'achat.
 *
 * Les numéros de requête (ZMSM-R-…) étaient jusqu'ici noyés dans les
 * descriptions de tâches. Ils ont désormais leur champ, ce qui permet de voir
 * d'un coup d'œil tout ce qui est en attente de commande ou de livraison.
 */
function Purchases({ users, currentUser, onOpenProject }) {
  const [achats, setAchats] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [filtre, setFiltre] = useState('tous');
  const [tacheOuverte, setTacheOuverte] = useState(undefined);

  const charger = () => {
    setChargement(true);
    api
      .get('/purchases')
      .then((res) => setAchats(res.data))
      .catch(() => setAchats([]))
      .finally(() => setChargement(false));
  };

  useEffect(charger, []);

  const filtres = useMemo(
    () => (filtre === 'tous' ? achats : achats.filter((a) => (a.purchase_status || 'a_lancer') === filtre)),
    [achats, filtre]
  );

  const compte = (statut) =>
    achats.filter((a) => (a.purchase_status || 'a_lancer') === statut).length;

  const enRetard = achats.filter((a) => a.is_overdue).length;

  if (chargement) return <p>Chargement...</p>;

  return (
    <div className="fade-in">
      {achats.length === 0 ? (
        <div className="empty-state">
          Aucune demande d'achat enregistrée.
          <div style={{ fontSize: 12.5, marginTop: 8, lineHeight: 1.6 }}>
            Pour qu'une tâche apparaisse ici, ouvrez-la et renseignez son
            <strong> numéro de requête</strong> dans la section « Demande d'achat ».
          </div>
        </div>
      ) : (
        <>
          <div className="page-header-row">
            <div className="filter-pills">
              <button
                className={`filter-pill ${filtre === 'tous' ? 'active' : ''}`}
                onClick={() => setFiltre('tous')}
              >
                Toutes ({achats.length})
              </button>
              {PURCHASE_STATUSES.map((s) => (
                <button
                  key={s}
                  className={`filter-pill ${filtre === s ? 'active' : ''}`}
                  onClick={() => setFiltre(s)}
                >
                  {PURCHASE_STATUS_LABELS[s]} ({compte(s)})
                </button>
              ))}
            </div>
          </div>

          {enRetard > 0 && (
            <div className="alerte-retard" style={{ marginBottom: 16 }}>
              <strong>{enRetard}</strong> demande{enRetard > 1 ? 's' : ''} d'achat
              {enRetard > 1 ? ' ont' : ' a'} dépassé son échéance.
            </div>
          )}

          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>N° requête</th>
                  <th>Article / tâche</th>
                  <th>Projet</th>
                  <th>Statut achat</th>
                  <th>Responsable</th>
                  <th>Échéance</th>
                </tr>
              </thead>
              <tbody>
                {filtres.map((a) => (
                  <tr key={a.id} className="clickable" onClick={() => setTacheOuverte(a)}>
                    <td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                      {a.purchase_request || <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>—</span>}
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>
                        {taskCode(a.id)}
                      </div>
                    </td>
                    <td>{a.name}</td>
                    <td style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{a.project_name}</td>
                    <td>
                      <span className={`badge ${PURCHASE_STATUS_BADGE[a.purchase_status] || 'badge-todo'}`}>
                        {PURCHASE_STATUS_LABELS[a.purchase_status] || 'À lancer'}
                      </span>
                    </td>
                    <td>{a.assignee_name}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <span className={a.is_overdue ? 'date-retard' : ''}>
                        {formatDate(a.due_date)}
                      </span>
                    </td>
                  </tr>
                ))}
                {filtres.length === 0 && (
                  <tr>
                    <td colSpan={6} className="empty-state">Aucune demande dans cette catégorie.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tacheOuverte !== undefined && (
        <TaskModal
          task={tacheOuverte}
          projectId={tacheOuverte?.project_id}
          users={users}
          currentUser={currentUser}
          onClose={() => setTacheOuverte(undefined)}
          onSaved={() => { setTacheOuverte(undefined); charger(); }}
          onDeleted={() => { setTacheOuverte(undefined); charger(); }}
        />
      )}
    </div>
  );
}

export default Purchases;
